import {
  IonButtons,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonContent,
  IonButton,
  IonIcon,
  IonToast,
  useIonViewWillLeave,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import DetailsSection from '../detailsSection/DetailsSection.component'
import BasicSection from '../basicSection/BasicSection.component'
import AdvancedSection from '../advancedSection/AdvancedSection.component'
import './Vitals.page.css'
import { useTranslation } from 'react-i18next'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { chevronBackOutline } from 'ionicons/icons'
import {
  checkQuickActionsRedirect,
  saveVitals,
  setShowDateTimeToast,
  setShowPTAToast,
  showAlert,
  syncDateTime,
} from '../store/vitals.actions'
import { useDispatch, useSelector } from 'react-redux'
import { IRootState } from '../../../store'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../utilities/object.util'
import { initialVitalsState } from '../store/vitals.reducers'
import { useGoBack } from '../../../utilities/navigation.util'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { VitalsConstants } from '../store/vitals.constants'
import { IRulesState } from '../../rules/store/rules.types'
import { getRulesSelector } from '../../rules/selectors'
import { clearValidationErrorsByPathList } from '../../../utilities/runRulesValidation.util'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { setDeleteProcedure } from '../../timeline/store/timeline.actions'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../components/toast/toastIcon.util'

const Vitals: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const vitals = useSelector((state: IRootState) => state.vitals)
  const { pta, date, time, showDateTimeToast, showPTAToast } = vitals
  const goBack = useGoBack()
  const rules: IRulesState = useSelector(getRulesSelector)
  const configuration: IConfigurationsState = useSelector(getConfigurationsSelector)
  const { datapointsConfig } = configuration
  const { DP_PAIN_QUALITY, DP_PAIN_QUALITY_REQ } = datapointsConfig

  const dateTimeValidationCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    if (hasStateChanged() && (!date || !time)) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const hasStateChanged = () => {
    const fieldsToFilter = [
      'key',
      'addToTimeline',
      'temperatureType',
      'showRepeatVitals',
      'reRunValidations',
      '__isRedirectedFrom',
      'customVitalLabel1',
      'customVitalLabel2',
      '__customVitalLabelExists',
    ]
    const currentState = filterOutObjectKeys(vitals, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialVitalsState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return true
    }
    return false
  }

  const isPainQualityEmpty = () => {
    const pq = vitals.painQuality
    if (!pq) return true
    return (
      (!pq.onset || pq.onset.trim() === '') &&
      !pq.onsetUnit &&
      !pq.field1 &&
      !pq.field2 &&
      !pq.field3 &&
      !pq.field4 &&
      !pq.field5 &&
      !pq.field6 &&
      !pq.field7 &&
      (!pq.comments || pq.comments.trim() === '')
    )
  }

  const painQualityRequiredCheck = () => {
    const painNum = vitals.pain ? Number(vitals.pain) : 0
    if (DP_PAIN_QUALITY === 0 || DP_PAIN_QUALITY_REQ === 0 || !vitals.pain) return false
    if (DP_PAIN_QUALITY === 1 && DP_PAIN_QUALITY_REQ === 1 && painNum > 0 && isPainQualityEmpty()) {
      dispatch(
        showAlert({
          header: '',
          message: t('vitals.painQuality.validationRequired'),
        })
      )
      return true
    }
    return false
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (painQualityRequiredCheck()) return
    if (!pta && (!date || !time)) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return
    }
    const isdateTimeValid = dateTimeValidationCheck()
    if (isdateTimeValid) {
      goBack()
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (painQualityRequiredCheck()) return

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              dispatch({ type: VitalsConstants.ADD_CURRENT_TIME })
              dispatch(syncDateTime())
              dispatch(saveVitals())
              rules.errors = clearValidationErrorsByPathList(rules.errors, [
                VitalsConstants.SET_DATE,
                VitalsConstants.SET_TIME,
              ])

              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: vitals.key,
                rowActionType: TimelineConstants.SET_VITAL_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
    } else {
      const isdateTimeValid = dateTimeValidationCheck()
      if (isdateTimeValid) {
        goBack()
      }
    }
  }
  const handleDateTimeToastDismiss = () => {
    dispatch(setShowDateTimeToast(false))
  }

  const handlePTAToastDismiss = () => {
    dispatch(setShowPTAToast(false))
  }

  useIonViewWillLeave(() => {
    // Don't reset state when navigating between vitals sub-pages
    const isNavigatingToVitalsSubPage = [
      TabConstants.VITALS_STROKE_SCORE,
      TabConstants.VITALS_PAIN_QUALITY,
      TabConstants.VITALS_PAIN_SCALES,
      TabConstants.VITALS_SEDATION_SCALE,
      TabConstants.VITALS_WITHDRAWAL_SCALES,
    ].includes(history.location.pathname)
    if (!isNavigatingToVitalsSubPage) {
      dispatch(checkQuickActionsRedirect())
    }
  })

  return (
    <IonPage id='VitalsPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='VitalsBackButton' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Vitals</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <DetailsSection />

        <BasicSection />

        <AdvancedSection isPHI />
      </IonContent>
      {showDateTimeToast && (
        <IonToast
          isOpen={showDateTimeToast}
          position='top'
          message={t('vitals.details.dateTimeToast')}
          {...getToastProps(ToastColor.Info)}
          duration={2000}
          onWillPresent={handlePTAToastDismiss}
          onDidDismiss={handleDateTimeToastDismiss}
        />
      )}
      {showPTAToast && (
        <IonToast
          isOpen={showPTAToast}
          position='top'
          message={t('vitals.details.ptaToast')}
          {...getToastProps(ToastColor.Info)}
          duration={2000}
          onWillPresent={handleDateTimeToastDismiss}
          onDidDismiss={handlePTAToastDismiss}
        />
      )}
    </IonPage>
  )
}

export default Vitals
